"use client";
import { Box, Container, Text, Stack, Button, Group, SimpleGrid } from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconPalette,
  IconUser,
  IconPhoto,
  IconShirt,
  IconSparkles,
} from "@tabler/icons-react";
import Image from "next/image";
import { PRICING } from "@/constants/pricing";
import { FeaturedIn } from "@/components/FeaturedIn";
import DemoCarousel from "@/components/DemoCarousel";
import { useEffect, ReactNode } from "react";
import { trackMetaPixelLandingPage } from "@/lib/metaPixelAnalytics";
import { Link } from "@/i18n/navigation";
import { trackCtaClick } from "@/lib/analytics";
import { useTranslations } from "next-intl";
import classes from "./home.module.css";

/* The 12 seasonal palettes (constants/index.tsx colorSeasons), compressed into
   the spectrum rail — the page's recurring color device. */
const DRAPES: { name: string; stops: [string, string, string] }[] = [
  { name: "Light Spring", stops: ["#FFDAB3", "#FBCEB1", "#F88379"] },
  { name: "True Spring", stops: ["#FFBF00", "#FF6F61", "#FF6347"] },
  { name: "Bright Spring", stops: ["#FFF700", "#FF9900", "#FF5E5B"] },
  { name: "Light Summer", stops: ["#FFD1DC", "#B4CFEC", "#C8A2C8"] },
  { name: "True Summer", stops: ["#D4A5A5", "#E0B0FF", "#5D6D8E"] },
  { name: "Cool Summer", stops: ["#C76B8A", "#3E8E8E", "#3B5998"] },
  { name: "Soft Autumn", stops: ["#D4AF37", "#C87C5A", "#808000"] },
  { name: "True Autumn", stops: ["#F4C430", "#FF7518", "#B87333"] },
  { name: "Deep Autumn", stops: ["#B87333", "#8A3324", "#4A1C17"] },
  { name: "Cool Winter", stops: ["#A5F2F3", "#007FFF", "#990066"] },
  { name: "True Winter", stops: ["#E5F9F9", "#0055FF", "#000000"] },
  { name: "Deep Winter", stops: ["#005F5F", "#000080", "#4A0E0E"] },
];

/* One dot per macro-season, drawn from the same palettes */
const SEASON_DOTS = ["#FF6F61", "#5D6D8E", "#B87333", "#0055FF"];
const SEASON_DOT_BY_NAME: Record<string, string> = {
  Spring: "#FF6F61",
  Summer: "#5D6D8E",
  Autumn: "#B87333",
  Winter: "#0055FF",
};

/* The hero tableau: one real customer-style selfie, and the same face draped
   by the actual product (drapeSamples/female1 — Cool Winter). */
const TABLEAU_CELLS = [
  { src: "/mainLanding/hero/drape-azure.webp", alt: "Same woman draped in azure blue" },
  { src: "/mainLanding/hero/drape-burgundy.webp", alt: "Same woman draped in burgundy" },
  { src: "/mainLanding/hero/drape-magenta.webp", alt: "Same woman draped in magenta" },
  { src: "/mainLanding/hero/drape-sapphire.webp", alt: "Same woman draped in sapphire" },
];

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className={classes.eyebrow}>
      <span className={classes.eyebrowDots}>
        {SEASON_DOTS.map((c) => (
          <span key={c} style={{ background: c }} />
        ))}
      </span>
      {children}
    </span>
  );
}

function SpectrumRail() {
  return (
    <div className={classes.rail} role="img" aria-label="The 12 seasonal color palettes">
      {DRAPES.map((d) => (
        <div
          key={d.name}
          className={classes.railSeg}
          title={d.name}
          style={{ background: `linear-gradient(90deg, ${d.stops[0]}, ${d.stops[1]} 55%, ${d.stops[2]})` }}
        />
      ))}
    </div>
  );
}

function Folio({ label, title, sub }: { label: string; title: string; sub?: string }) {
  return (
    <div className={classes.folio}>
      <span className={classes.folioLabel}>{label}</span>
      <div>
        <h2 className={classes.folioTitle}>{title}</h2>
        {sub && <p className={classes.folioSub}>{sub}</p>}
      </div>
    </div>
  );
}

export default function Content() {
  const t = useTranslations("home");
  const tDraping = useTranslations("virtualColorDraping");
  // Honest volume claim — shared with the ai-color-analysis landing so the
  // number lives in one translation key per locale
  const tProof = useTranslations("aiColorAnalysis.landing");

  useEffect(() => {
    // Track landing page view
    trackMetaPixelLandingPage();
  }, []);

  const offerings = [
    {
      icon: IconPalette,
      label: t("offeringColorAnalysis"),
      desc: t("offeringColorAnalysisDesc"),
      href: "/ai-color-analysis",
      swatches: ["#F4C430", "#C76B8A", "#0055FF"],
      cta: "home_offer_color_analysis",
    },
    {
      icon: IconUser,
      label: t("offeringKibbe"),
      desc: t("offeringKibbeDesc"),
      href: "/kibbe-body-type-test",
      swatches: ["#D4A5A5", "#5D6D8E", "#1e293b"],
      cta: "home_offer_kibbe",
    },
    {
      icon: IconPhoto,
      label: t("offeringDraping"),
      desc: tDraping("meta.title"),
      href: "/virtual-color-draping",
      swatches: ["#E0B0FF", "#990066", "#4A1C17"],
      cta: "home_offer_free_draping",
    },
    {
      icon: IconShirt,
      label: t("offeringOutfits"),
      desc: t("offeringOutfitsDesc"),
      href: "/ai-outfit-generator",
      swatches: ["#B5EAD7", "#3E8E8E", "#000080"],
      cta: "home_offer_outfits",
    },
  ];

  const steps: { img: string; title: string; desc: string; alt: string; pos?: string }[] = [
    { img: "/mainLanding/hero/selfie.webp", title: t("how1Title"), desc: t("how1Desc"), alt: "A phone selfie in a bedroom" },
    // Wide report screenshot — pin to its left edge so the season headline + trait chips stay in frame
    { img: "/resultPreviews/hero.webp", title: t("how2Title"), desc: t("how2Desc"), alt: "The color analysis report", pos: "left 30%" },
    { img: "/mainLanding/hero/drape-magenta.webp", title: t("how3Title"), desc: t("how3Desc"), alt: "The same face draped in magenta" },
  ];

  const quotes = [
    { text: t("quote1"), author: "KB", season: "Deep Autumn", dot: SEASON_DOT_BY_NAME.Autumn },
    { text: t("quote2"), author: "Derek L.", season: "Warm Spring", dot: SEASON_DOT_BY_NAME.Spring },
    { text: t("quote3"), author: "Zara K.", season: "Cool Winter", dot: SEASON_DOT_BY_NAME.Winter },
  ];

  return (
    <Container size="xl" py={{ base: "5rem", sm: "xl" }}>
      {/* ============ Hero — promise left, transformation tableau right ============ */}
      <Box className={classes.hero}>
        <Box>
          <Eyebrow>{tProof("weeklyProof")}</Eyebrow>
          <h1 className={classes.heroTitle}>
            {t.rich("hero2Title", {
              accent: (chunks) => <span className={classes.heroAccent}>{chunks}</span>,
            })}
          </h1>
          <p className={classes.heroSubtitle}>{t("hero2Subtitle")}</p>

          <Stack gap="sm" mt={30} align="flex-start">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("home_hero")}
              size="xl"
              radius="xl"
              rightSection={<IconArrowRight size={20} />}
              style={{
                fontSize: "1.05rem",
                fontWeight: 500,
                padding: "1rem 2rem",
                height: "auto",
                background: "#16181d",
                color: "white",
              }}
            >
              {t("ctaPrimary")}
            </Button>

            <Button component={Link} href="/virtual-color-draping" variant="light" radius="xl" size="md"
              onClick={() => trackCtaClick("home_free_draping_hero")}>
              {t("offeringDraping")}
            </Button>

            <Text size="sm" style={{ color: "#5a5f6b" }}>
              {t("ctaSecondaryPrefix")}{" "}
              <Link
                href="/color-analysis-quiz"
                onClick={() => trackCtaClick("home_quiz_link")}
                style={{ color: "#b3265c", textDecoration: "none", fontWeight: 500 }}
              >
                {t("ctaSecondaryLink")}
              </Link>
            </Text>

            {/* Trust indicators */}
            <Group gap="lg" mt={6}>
              {[t("trustInstant"), t("trustGuarantee"), t("trustColors")].map((label) => (
                <Group key={label} gap={4}>
                  <IconCheck size={14} style={{ color: "#22c55e" }} />
                  <Text size="xs" style={{ color: "#5a5f6b" }}>
                    {label}
                  </Text>
                </Group>
              ))}
            </Group>
          </Stack>
        </Box>

        {/* Signature: her bedroom selfie → the product's drapes of the same face */}
        <Box className={classes.tableau}>
          <Box className={classes.tabSide}>
            <Box className={classes.tabSelfie}>
              <Image
                src="/mainLanding/hero/selfie.webp"
                alt="A regular phone selfie"
                fill
                priority
                sizes="(max-width: 980px) 40vw, 20vw"
                style={{ objectFit: "cover" }}
              />
            </Box>
            <span className={classes.tabLabel}>{t("tabInputLabel")}</span>
          </Box>
          <Box className={classes.tabArrow} aria-hidden="true">
            <IconArrowRight size={20} />
          </Box>
          <Box className={classes.tabSide}>
            <Box className={classes.tabGrid}>
              {TABLEAU_CELLS.map((cell, i) => (
                <Box key={cell.src} className={classes.tabCell} style={{ animationDelay: `${0.35 + i * 0.12}s` }}>
                  <Image
                    src={cell.src}
                    alt={cell.alt}
                    fill
                    priority={i < 2}
                    sizes="(max-width: 980px) 30vw, 16vw"
                    style={{ objectFit: "cover" }}
                  />
                </Box>
              ))}
            </Box>
            <span className={classes.tabLabel}>{t("tabOutputLabel")}</span>
          </Box>
        </Box>
      </Box>

      {/* The 12 seasons, as a rail */}
      <SpectrumRail />

      <Box mt={{ base: 36, md: 48 }}>
        <FeaturedIn />
      </Box>

      {/* ============ How it works — a true sequence, so it's numbered ============ */}
      <Folio label={t("folioProcess")} title={t("howTitle")} sub={t("howSub")} />
      <Box className={classes.howGrid}>
        {steps.map((s, i) => (
          <Box key={s.title} className={classes.howStep}>
            <span className={classes.howNum}>{String(i + 1).padStart(2, "0")}</span>
            <Box className={classes.howImg}>
              <Image
                src={s.img}
                alt={s.alt}
                fill
                sizes="(max-width: 900px) 90vw, 30vw"
                style={{ objectFit: "cover", objectPosition: s.pos ?? "center" }}
              />
            </Box>
            <h3 className={classes.howTitle}>{s.title}</h3>
            <p className={classes.howDesc}>{s.desc}</p>
          </Box>
        ))}
      </Box>

      {/* ============ Proof — before/after carousel ============ */}
      <Folio label={t("folioProof")} title={t("resultsTitle")} sub={t("resultsSubtitle")} />
      <Box mt={40}>
        <DemoCarousel />
      </Box>

      {/* ============ Offerings ============ */}
      <Folio label={t("folioTools")} title={t("offeringsTitle")} sub={t("offeringsSubtitle")} />
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing={{ base: "sm", md: "lg" }} mt={40}>
        {offerings.map((item) => (
          <Link
            key={item.label}
            href={item.href}
            onClick={() => trackCtaClick(item.cta)}
            className={classes.offerCard}
          >
            <span
              className={classes.offerBand}
              style={{ background: `linear-gradient(100deg, ${item.swatches[0]}, ${item.swatches[1]} 55%, ${item.swatches[2]})` }}
            />
            <span className={classes.offerBody}>
              <item.icon size={22} color="#16181d" stroke={1.6} />
              <span className={classes.offerTitle}>{item.label}</span>
              <span className={classes.offerDesc}>{item.desc}</span>
              <span className={classes.offerArrow}>
                <IconArrowRight size={16} />
              </span>
            </span>
          </Link>
        ))}
      </SimpleGrid>

      {/* Free draping is the community's entry point; the paid analysis remains above. */}
      <Box mt={{ base: 72, md: 104 }}>
        <Box className={classes.clubPanel}>
          <Stack gap="md" maw={700}>
            <h2 className={classes.clubTitle}>{tDraping("meta.title")}</h2>
            <Text fz="md" lh={1.65} c="gray.3">{tDraping("meta.description")}</Text>
            <Button component={Link} href="/virtual-color-draping" radius="xl" size="lg"
              onClick={() => trackCtaClick("home_free_draping_panel")}
              rightSection={<IconArrowRight size={18} />} w="fit-content">
              {t("offeringDraping")}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ============ Word of mouth — real quotes, editorial pull-quote treatment ============ */}
      <Folio label={t("folioWord")} title={t("quotesTitle")} />
      <Box className={classes.quoteGrid}>
        {quotes.map((q) => (
          <Box key={q.author} className={classes.quoteCard}>
            <span className={classes.quoteMark} aria-hidden="true">
              &ldquo;
            </span>
            <p className={classes.quoteText}>{q.text}</p>
            <span className={classes.quoteMeta}>
              {q.author}
              <span className={classes.quoteChip}>
                <i style={{ background: q.dot }} />
                {q.season}
              </span>
            </span>
          </Box>
        ))}
      </Box>

      {/* ============ Bundle ============ */}
      <Box mt={{ base: 72, md: 104 }}>
        <Box className={classes.bundlePanel}>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" style={{ alignItems: "center" }}>
            <Stack gap="sm">
              <Eyebrow>{t("bundleBadge")}</Eyebrow>
              <h2 className={classes.sectionTitle}>
                {t.rich("bundleTitle", {
                  accent: () => <span className={classes.heroAccent}>{t("bundleTitleAccent")}</span>,
                })}
              </h2>
              <Text fz="md" lh={1.65} style={{ color: "#5a5f6b" }}>
                {t("bundleDesc")}
              </Text>

              <Stack gap={8} mt="xs">
                {[
                  { icon: IconPalette, label: t("bundleColors") },
                  { icon: IconUser, label: t("bundleBodyType") },
                  { icon: IconPhoto, label: t("bundleDraping") },
                  { icon: IconShirt, label: t("bundleOutfits") },
                ].map((item) => (
                  <Group key={item.label} gap={8}>
                    <item.icon size={16} color="#16181d" stroke={1.6} />
                    <Text fz="sm" style={{ color: "#475569" }}>
                      {item.label}
                    </Text>
                  </Group>
                ))}
              </Stack>

              <Button
                component={Link}
                href="/style-profile-bundle"
                onClick={() => trackCtaClick("home_bundle")}
                size="md"
                radius="xl"
                mt="sm"
                w="fit-content"
                rightSection={<IconArrowRight size={16} />}
                style={{ background: "#16181d", color: "white" }}
              >
                {t("bundleCta")}
              </Button>
            </Stack>

            <Box ta={{ base: "center", md: "right" }}>
              <span className={classes.priceDisplay}>{t("bundlePrice")}</span>
              <Text fz="sm" td="line-through" style={{ color: "#8b8b98" }}>
                {t("bundleOriginalPrice")}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      {/* ============ Closing band ============ */}
      <Box className={classes.band} mb={{ base: 40, md: 60 }}>
        <div className={classes.bandRail} aria-hidden="true">
          {DRAPES.map((d) => (
            <span key={d.name} style={{ background: d.stops[1] }} />
          ))}
        </div>
        <h2 className={classes.bandTitle}>{t("bandTitle")}</h2>
        <p className={classes.bandSub}>{t("bandSub")}</p>
        <Button
          component={Link}
          href="/ai-color-analysis"
          onClick={() => trackCtaClick("home_footer_cta")}
          size="lg"
          radius="xl"
          rightSection={<IconArrowRight size={18} />}
          style={{ background: "#f1f5f9", color: "#16181d", fontWeight: 600 }}
        >
          {t("ctaPrimary")}
        </Button>
      </Box>
    </Container>
  );
}
